import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { CsvImportModal } from '../../components/data/CsvImportModal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatCurrency, formatNumber } from '../../utils/formatters.js';
import { sumBy } from '../../utils/insightStats.js';

import { ProductCreateModal } from './ProductCreateModal.jsx';
import { ProductEditModal } from './ProductEditModal.jsx';

const CSV_COLUMNS = [
  { key: 'sku', label: 'SKU' },
  { key: 'name', label: 'Name' },
  { key: 'unitOfMeasure', label: 'Unit' },
  { key: 'listPrice', label: 'ListPrice' },
  { key: 'reorderPoint', label: 'ReorderPoint' },
];

const CSV_FIELDS = [
  { field: 'sku', label: 'SKU', aliases: ['artikelnummer', 'code'], required: true },
  { field: 'name', label: 'Name', aliases: ['produkt', 'bezeichnung'], required: true },
  { field: 'unitOfMeasure', label: 'Unit', aliases: ['einheit', 'uom'] },
  { field: 'listPrice', label: 'ListPrice', aliases: ['preis', 'price'] },
  { field: 'reorderPoint', label: 'ReorderPoint', aliases: ['meldebestand', 'reorder'] },
];

export function ProductsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const { data: antwort, isLoading, error } = useQuery({
    queryKey: ['inventory', 'products', q],
    queryFn: () => inventoryApi.listProducts({ q: q || undefined }),
  });
  const data = antwort?.items || [];
  const meta = antwort?.meta;

  const palette = useChartPalette();
  const rows = data || [];
  const stats = useMemo(() => {
    const active = rows.filter((r) => r.isActive).length;
    const priceSum = sumBy(rows, (r) => r.listPrice);
    return {
      active,
      inactive: rows.length - active,
      avgPrice: rows.length ? priceSum / rows.length : 0,
    };
  }, [rows]);

  const columns = [
    { key: 'sku', label: t('inv.products.col.sku') },
    { key: 'name', label: t('col.name') },
    {
      key: 'productType',
      label: t('inv.products.modal.type'),
      render: (r) => (
        <span className={r.productType === 'SERVICE' ? '' : 'muted'}>
          {t(`product.type.${r.productType || 'GOOD'}`)}
        </span>
      ),
    },
    {
      key: 'unitOfMeasure',
      label: t('col.unit'),
      // Translate known unit codes (EACH → Stück); unknown codes show as-is.
      render: (r) => {
        const key = `uom.unit.${r.unitOfMeasure}`;
        const label = t(key);
        return label === key ? r.unitOfMeasure : label;
      },
    },
    { key: 'listPrice',    label: t('inv.products.col.price'),   align: 'right', render: (r) => formatCurrency(r.listPrice) },
    { key: 'reorderPoint', label: t('inv.products.col.reorder'), align: 'right', render: (r) => formatNumber(r.reorderPoint) },
    { key: 'isActive', label: t('col.status'), render: (r) => <Badge status={r.isActive ? 'ACTIVE' : 'INACTIVE'} /> },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <Button size="sm" variant="ghost" onClick={() => setEditTarget(r)}>
          {t('common.edit')}
        </Button>
      ),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('inv.products.title')}</h1>
          <p className="muted">{t('inv.products.subtitle')}</p>
        </div>
        <div className="page-actions">
          <CsvExportButton rows={data || []} columns={CSV_COLUMNS} filename="produkte" />
          <Button variant="default" onClick={() => setImportOpen(true)}>↑ {t('csv.import')}</Button>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('inv.products.action.new')}
          </Button>
        </div>
      </header>

      {/* Die Kennzahlen summieren die geholten Zeilen. Sind das nicht alle,
          ist jede Zahl darunter zu klein. */}
      {meta?.total > data.length && (
        <div className="alert warning">
          {t('insight.gekuerzt', { gezeigt: data.length, gesamt: meta.total })}
        </div>
      )}
      {rows.length > 0 && (
        <InsightBar
          title={t('insight.byStatus')}
          labels={[t('status.ACTIVE'), t('status.INACTIVE')]}
          values={[stats.active, stats.inactive]}
          colors={[palette?.series?.[1], palette?.axis]}
          metrics={[
            { label: t('insight.count'), value: rows.length },
            { label: t('insight.active'), value: stats.active },
            { label: t('inv.products.insight.avgPrice'), value: formatCurrency(stats.avgPrice) },
            { label: t('insight.inactive'), value: stats.inactive },
          ]}
        />
      )}
      <div className="toolbar">
        <input
          className="input"
          placeholder={t('inv.products.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} error={error} meta={meta} emptyMessage={t('empty.products')} />
      <ProductCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ProductEditModal product={editTarget} onClose={() => setEditTarget(null)} />
      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={t('inv.products.action.new')}
        fields={CSV_FIELDS}
        onImportRow={(row) =>
          inventoryApi.createProduct({
            sku: row.sku,
            name: row.name,
            unitOfMeasure: row.unitOfMeasure || 'EACH',
            listPrice: row.listPrice || '0',
            reorderPoint: Number(row.reorderPoint) || 0,
          })
        }
        onDone={() => queryClient.invalidateQueries({ queryKey: ['inventory', 'products'] })}
      />
    </section>
  );
}
